/**
 * The company's break mode as words (TF-BREAK-DISPLAY, Codex wording 2026-09-14; three modes since
 * plan 0032, 2026-10-06), so the settings page and the employee card say the same thing. An older
 * row without `break_mode` reads its `lunch_included`: true is automatic, false is recorded.
 */
import { T } from '@/i18n';
import type { BreakMode } from '@timefly/core';

export const BREAK_MODES: BreakMode[] = ['auto', 'punched', 'punched_min'];

export const modeOf = (t: { break_mode?: BreakMode; lunch_included?: boolean } | undefined): BreakMode =>
  t?.break_mode ?? (t?.lunch_included === false ? 'punched' : 'auto');

export const breakCalcLabel = (m: BreakMode): string =>
  m === 'auto'
    ? T('Pausen trekkes alltid (ubetalt)')
    : m === 'punched'
      ? T('Pausen trekkes bare når den stemples')
      : T('Stemplet pause trekkes, men alltid minst den faste');

export const breakCalcHelp = (m: BreakMode, min = 30): string =>
  m === 'auto'
    ? min === 0
      ? T('Lengden er 0, så pausen er betalt: ingenting trekkes.')
      : T(
          '{min} min trekkes fra vakter over 5,5 timer, uansett om pausen stemples. Et 8-timers skift blir {h, number} timer. Sett lengden til 0 hvis pausen er betalt.',
          { min, h: Math.round((8 - min / 60) * 100) / 100 },
        )
    : m === 'punched'
      ? T(
          'Bare pausen den ansatte stempler, trekkes. Glemmer de å stemple, trekkes ingenting og hele vakten betales.',
        )
      : T(
          'Den stemplede pausen trekkes, men aldri mindre enn den faste pauselengden på vakter over 5,5 timer. En glemt pause trekkes likevel.',
        );

/** The break as the employee reads it on Min side. */
export const breakForEmployee = (
  t: { break_mode?: BreakMode; lunch_included?: boolean; lunch_length_minutes?: number | null } | undefined,
): string => {
  const min = t?.lunch_length_minutes ?? 30;
  const m = modeOf(t);
  return m === 'auto'
    ? T('{min} min trekkes automatisk', { min })
    : m === 'punched'
      ? T('Du stempler pausen selv')
      : T('Du stempler pausen selv, minst {min} min trekkes', { min });
};

/**
 * The Pause settings as the owner chose them (prototype A, 2026-10-28): paid or unpaid, the length for
 * unpaid, and whether the staff clock the break. Betalt is auto with length 0, Ubetalt is auto, Ubetalt
 * with clocking is punched_min. The old punched mode (a forgotten break is paid) is `legacy`: no card
 * is chosen and the page offers the move to unpaid.
 */
export type BreakChoice = { legacy: boolean; paid: boolean; len: number; clocks: boolean };

export const breakChoiceOf = (
  t: { break_mode?: BreakMode; lunch_included?: boolean; lunch_length_minutes?: number | null } | undefined,
): BreakChoice => {
  const m = modeOf(t);
  const len = t?.lunch_length_minutes ?? 30;
  return { legacy: m === 'punched', paid: m === 'auto' && len === 0, len, clocks: m === 'punched_min' };
};

/** Paid time in words: 7,5 t, or 7 t 53 min when it is not a whole quarter. */
export const paidHours = (min: number): string =>
  min % 15
    ? T('{t} t {m} min', { t: Math.floor(min / 60), m: min % 60 })
    : T('{h, number} t', { h: min / 60 });

/** One line of «Slik blir det»; `bold` is the paid time inside `text`, `dim` the short shift without a break. */
export type BreakExampleLine = { text: string; bold: string; dim?: boolean };

const SHIFT = 480; // 08:00–16:00
const SHORT = 300; // 10:00–15:00, under 5,5 t

/** The example lines for a choice: what an 8-hour and a 5-hour shift are paid. Mirrors the rule in words. */
export function breakExample(c: BreakChoice): BreakExampleLine[] {
  const line = (min: number, say: (h: string) => string): BreakExampleLine => {
    const bold = paidHours(min);
    return { text: say(bold), bold };
  };
  const L: BreakExampleLine[] = [];
  if (c.legacy) {
    L.push(
      line(SHIFT - 30, (h) => T('En vakt 08:00–16:00 med stemplet pause på 30 min blir {h} betalt.', { h })),
    );
    L.push(line(SHIFT, (h) => T('Glemmer den ansatte å stemple pausen, blir den {h} betalt.', { h })));
  } else if (c.paid) {
    L.push(line(SHIFT, (h) => T('En vakt 08:00–16:00 blir {h} betalt. Pausen er arbeidstid.', { h })));
  } else if (!c.clocks) {
    L.push(
      line(SHIFT - c.len, (h) =>
        T('En vakt 08:00–16:00 blir {h} betalt ({min} min trekkes).', { h, min: c.len }),
      ),
    );
  } else {
    const long = Math.max(45, c.len + 15);
    L.push(
      line(SHIFT - long, (h) =>
        T('En vakt 08:00–16:00 med stemplet pause på {long} min blir {h} betalt.', { h, long }),
      ),
    );
    L.push(
      line(SHIFT - c.len, (h) =>
        T('Stemples ingen pause, trekkes {min} min likevel: {h} betalt.', { h, min: c.len }),
      ),
    );
  }
  L.push({
    ...line(SHORT, (h) => T('En vakt 10:00–15:00 blir {h} betalt: under 5,5 t, ingen pause.', { h })),
    dim: true,
  });
  return L;
}
